/* eslint-disable react/function-component-definition */

/* Global imports. */
import React from "react";
import { graphql } from "gatsby";

/* Class imports. */
import Breadcrumb from "@classes/Breadcrumb";

/* Component imports. */
import AnchorLinksIndex from "@src/components/AnchorLinksIndex";
import Breadcrumbs from "@components/Breadcrumbs";
import KontentRichText from "@components/KontentRichText";
import Layout from "@components/Layout";
import NewsletterHelper from "@components/NewsletterHelper"
import RelatedArticles from "@components/RelatedArticles";
import SEO from "@components/SEO";
import { useThemeOptions } from "@src/hooks/useThemeOptions"

/* Model imports. */
import { KontentArticle, KontentTemplate } from "@models/kontent";
import { PanMacmillanEdition } from "@src/models/panmacmillan";

/* Other imports. */
import { isEmpty, stripHTML } from "@src/helpers";
import { getCategorySlug } from "@src/kontent";
import {cleanImagePathForImageKit} from "@src/imagekit" 

/* Imports from within current directory. */
import Sidebar from "./Sidebar";
import ImageKit from "@src/components/ImageKit";
import { useSiteMetadata } from "@src/hooks/useSiteMetaData";

const Template = ({ data, pageContext }: any) => {
  const template: KontentTemplate = data.kontentItemArticle;
  const article: KontentArticle = template.elements;
  const editions: Array<PanMacmillanEdition> = data.allPanMacmillanEdition.edges;
  const latestArticles:Array<KontentArticle> = data.allKontentItemArticle.nodes;
  const relatedArticles:Array<KontentArticle> = article.related_articles.value && article.related_articles.value.length > 0 ? article.related_articles.value : latestArticles
  let breadcrumbs = [];

  if(article.article_type.value[0] && article.article_type.value[0].codename === "news") {
    breadcrumbs = [
      new Breadcrumb("Home", "/"),
      new Breadcrumb("News", "/news"),
    ];
    breadcrumbs && breadcrumbs.push(new Breadcrumb(article.title.value, "#"))
  }
  else {
    breadcrumbs = [
      new Breadcrumb("Home", "/"),
      new Breadcrumb("Articles", "/blogs"),
    ];
  
    article.article_categories && article.article_categories.value[0] && article.article_categories.value[0].codename && breadcrumbs.push(new Breadcrumb(
      article.article_categories.value[0].name,
      `/blogs/${getCategorySlug(article.article_categories.value[0].codename)}`
    ))
  
    breadcrumbs && breadcrumbs.push(new Breadcrumb(article.title.value, "#"))
  }


  const imagePath = !isEmpty(article.image.value) && cleanImagePathForImageKit(article.image.value[0].url)
  const imageImageKit = !isEmpty(article.image.value) && ImageKit({image:`tr:f-auto,w-740,pr-true/${imagePath}`})
  const newsletterType = article.newsletter_forms__newsletter_form.value && article.newsletter_forms__newsletter_form.value[0] && article.newsletter_forms__newsletter_form.value[0].system && article.newsletter_forms__newsletter_form.value[0].system.codename;
  const newsletterCategory = article.article_categories.value && article.article_categories.value[0] && article.article_categories.value[0].codename
  const { displayArticleFooterEmailSignUp  } = useThemeOptions()
  const { useMailChimpFooterNewsletter } = useSiteMetadata()

  return (
    <Layout>
      <main id="article" className="container-main">
        <div className="mb-2 md:mb-6">
          <Breadcrumbs breadcrumbs={breadcrumbs} />
        </div>
        <div className="grid grid-cols-12">
          <Sidebar
            title={article.title.value}
            date={article.display_date.value}
            content={article.content.value}
            contributorName={article.article_contributor.value[0] && article.article_contributor.value[0].elements.name.value}
            contributorUrl={article.article_contributor.value[0] && article.article_contributor.value[0].elements.website_url.value !== "" ? article.article_contributor.value[0].elements.website_url.value : undefined }
            slug={template.fields.slug}
            className="hidden col-span-2 md:block"
          />
            <article data-publisheddate={article.display_date.value} data-articletype={article.article_type.value[0] && article.article_type.value[0].codename} className="col-span-12 md:col-span-8 md:col-start-4">
              {/* Title */}
              {article.title.value && (
                <h1>{article.title.value}</h1>
              )}

              {/* Blurb */}
              {article.blurb.value && (
                <h4 className="!mb-4 font-sans text-lg font-normal !leading-6 sm:!leading-8 sm:text-[21px] md:text-2xl p-inherit article-blurb"
                dangerouslySetInnerHTML={{ __html: article.blurb.value }}
              />
              )}

              {/* Sidebar for mobile */}
               <Sidebar
                title={article.title.value}
                date={article.display_date.value}
                content={article.content.value}
                contributorName={article.article_contributor.value[0] && article.article_contributor.value[0].elements.name.value}
                contributorUrl={article.article_contributor.value[0] && article.article_contributor.value[0].elements.website_url.value !== "" ? article.article_contributor.value[0].elements.website_url.value : undefined }
                slug={template.fields.slug}
                className="flex flex-row flex-wrap col-span-12 mb-4 md:hidden"
              />

              {/* Image */}
              {!isEmpty(article.image.value) && (
                <div className="my-4 md:my-8">
                  <img src={imageImageKit} alt={article.image.value[0] && article.image.value[0].description} width="1000" height="563" loading="eager" fetchpriority="high" />
                </div>
              )}

              {/* Introduction */}
              {article.content_intro && (
                <div className="mb-4 sm:mb-8 font-sans text-xl font-bold leading-7 children-font-sans children-article-intro">
                  <KontentRichText value={article.content_intro} />
                  {<AnchorLinksIndex value={article.content} /> }
                </div>
              )}

              {/* Content */}
              {article.content && (
                <div id="primary-content" className="mb-4 kontent--richtext">
                  <KontentRichText value={article.content} editions={editions} referringSlug={template.fields.slug} maxImageWidth={700} />
                </div>
              )}
            </article>
            {displayArticleFooterEmailSignUp && !useMailChimpFooterNewsletter &&
            <div className="col-span-12 sm:col-span-8 sm:col-start-4 article-list--sidebar">
            <NewsletterHelper 
              id="article" 
              newsletterType={newsletterType ? newsletterType : newsletterCategory} 
              imprint={article.imprint} 
              hide={article.newsletter_forms__hide_default_newsletter_form__if_applicable_.value && article.newsletter_forms__hide_default_newsletter_form__if_applicable_.value[0]}
              newsletterLocation="article footer"
              />
            </div>
            }
          </div>
          <div className="w-full">
            {/* Related Articles */}
                <div className="grid grid-cols-12 mt-16 mb-4">
                  <RelatedArticles
                    format="portrait"
                    articleSummaries={relatedArticles}
                    sectionClass="col-span-12"
                    listClass="flex flex-col sm:flex-row w-full gap-8"
                    componentID="related-articles-articles"
                  />
                </div>
          </div>
      </main>
    </Layout>
  );
};

export default Template;

export const Head = ({data}) => {
  const template: KontentTemplate = data.kontentItemArticle;
  const article: KontentArticle = template.elements;
  const imagePath = !isEmpty(article.image.value) && cleanImagePathForImageKit(article.image.value[0].url)
  const imageImageKit = !isEmpty(article.image.value) && ImageKit({image: `tr:f-auto,w-740,pr-true/${imagePath}`})

  function articleImageUrl() {
    return imageImageKit ? imageImageKit : article.image.value[0] && article.image.value[0].url
  }

  const articleOpenGraphImage = article.seo__open_graph_image.value && article.seo__open_graph_image.value[0] && article.seo__open_graph_image.value && article.seo__open_graph_image.value[0].url

  return (
    <SEO
        title={article.title.value}
        description={article.seo__description.value}
        image={articleOpenGraphImage ? articleOpenGraphImage : articleImageUrl()}
        contentType="article"
        url={template.fields.slug}
        displayDate={article.display_date_no_format.value}
        modifiedDate={template.system.last_modified}
        noIndex={article.seo__no_index.value && article.seo__no_index.value[0] && article.seo__no_index.value[0].codename == "noindex" ? true : false}
        openGraphTitle={article.seo__open_graph_title.value}
        openGraphDescription={article.seo__open_graph_description.value}
        twitterCardTitle={article.seo__twitter_cards_title.value}
        twitterCardDescription={article.seo__twitter_cards_description.value}
      />
  )
}

/* GraphQL schema. */
export const query = graphql`
  query articleQuery(
      $slug: String!
      $editions: [String]
      $category: String
    ) {
    kontentItemArticle(
      elements: { url_slug: { value: { eq: $slug } } }
    ) {
      system {
        last_modified
      }
      fields {
        slug
      }
      elements {
        article_type {
          value {
            codename
          }
        }
        url_slug {
          value
        }
        seo__no_index {
          value {
            codename
          }
        }
        seo__description {
          value
        }
        seo__title {
          value
        }
        seo__open_graph_title {
          value
        }
        seo__open_graph_description {
          value
        }
        seo__open_graph_image {
          value {
            url
            name
            description
            width
            height
          }
        }
        seo__twitter_cards_title {
          value
        }
        seo__twitter_cards_description {
          value
        }
        imprint {
          value {
            codename
          }
        }
        article_categories {
          value {
            codename
            name
          }
        }
        newsletter_forms__hide_default_newsletter_form__if_applicable_ {
          value {
            codename
          }
        }
        newsletter_forms__newsletter_form {
          value {
            system {
              codename
            }
          }
        }
        title {
          value
        }
        blurb {
          value
        }
        image {
          value {
            url
            name
            description
            width
            height
          }
        }
        display_date {
          value(formatString: "DD/MM/YYYY")
        }
        display_date_no_format: display_date {
          value
        }
        content_intro {
          value
          modular_content {
            id
            system {
              type
              name
              codename
            }
          }
          links {
            url_slug
            type
            link_id
            codename
          }
          images {
            width
            url
            image_id
            height
            description
          }
        }
        content {
          modular_content {
            id
            system {
              name
              type
              codename
            }
            ... on kontent_item_anchor_links {
              ... modularKonteItemAnchorLinks
            }
            ... on kontent_item_featured_resources {
              ... modularKontentItemFeaturedResources
            }
            ... on kontent_item_book_spotlight_collection {
              ...modularKontentItemBookSpotlightCollection
            }
            ... on kontent_item_banner {
              ... modularKontentItemBanner
            }
            ... on kontent_item_don_t_miss {
              ... modularKontentItemDontMiss
            }
            ... on kontent_item_twitter_embedded_timeline {
              ...modularKontentTwitterEmbeddedTimeline
            }
            ... on kontent_item_external_featured_content {
               ... modularKontentItemExternalFeaturedContent
            }
            ... on kontent_item_button_link {
              ... modularKontentItemButtonLink
            }
            ... on kontent_item_book_spotlight {
              ... modularKontentItemBookSpotlight
            }
            ... on kontent_item_youtube_embed {
              ... modularKontentItemYouTubeEmbed
            }
            ... on kontent_item_soundcloud_embed__ {
              ... modularKontentItemSoundCloudEmbed
            }
            ... on kontent_item_quote {
              ... modularKontentItemQuote
            }
            ... on kontent_item_instagram_embed__ {
              ... modularKontentItemInstagramEmbed
            }
            ... on kontent_item_embed___iframe {
              ... modularKontentItemIframe
            }
            ... on kontent_item_inline_image {
              ... modularKontentItemInlineImage
            }
            ... on kontent_item_downloadable_resource {
              ... modularKontentItemDownloadableResource
            }
            ... on kontent_item_recipe_article {
              ... modularKontentItemRecipe
            }
            ... on kontent_item_featured_articles {
              ... modularKontentItemFeaturedArticles
            }
            ... on kontent_item_section_break {
              ... modularKontentItemSectionBreak
            }
          }
          value
          links {
            url_slug
            type
            link_id
            codename
          }
          images {
            width
            url
            image_id
            height
            description
          }
        }
        article_contributor {
          value {
            ... on kontent_item_article_contributor {
              id
              elements {
                name {
                  value
                }
                website_url {
                  value
                }
              }
            }
          }
        }
        related_articles {
          value {
            ... on kontent_item_article {
                ... allKontentItemArticles
            }
          }
        }
      }
    }
    allPanMacmillanEdition(filter: {isbn: {in: $editions}}) {
      edges {
        node {
          ... basicEditionDetails
          alternateEditions {
            isbn
            format
            titleFull
            primaryContributorName
            fields {
              slug
            }
            isPrimaryEdition
            textContents {
              textType
              text
              sourceTitle
            }
            imprintName
            format
            dimensions {
              width
              height
            }
            contributors {
              contributorID
              contributorRole
              contributorDetails {
                personName
                slug
              }
            }
          }
          primaryContributorName
          textContents {
            textType
            text
            sourceTitle
          }
          imprintName
          format
          dimensions {
            width
            height
          }
        }
      }
    }
    allKontentItemArticle(
      filter: {elements: {article_categories: {value: {elemMatch: {codename: {eq: $category}}}}, url_slug: {value: {ne: $slug}}}}
      limit: 3
      sort: {elements: {display_date: {value: DESC}}}
    ) {
      nodes {
        ... allKontentItemArticles
      }
    }
    allKontentItemNewsletterForm {
      edges {
        node {
          system {
            codename
          }
        }
      }
    }
  }
`